/**
 * @file `/courses` — the learner's enrolled course.
 * @module @labcraft/lab/ui/pages/courses
 *
 * Umbrella #927 / part #940. "Enrolled" = the current-engagement course
 * only (locked decision: there's no enrollment table, so the
 * `currentChapter` pointer is the enrollment signal). The full list is
 * `/catalog`. With no current chapter this renders a prompt into the
 * catalog; otherwise a single card into the course landing.
 */

import { useEffect, useState, type JSX } from 'react'
import { useNavigate } from 'react-router'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { CourseCatalogEntry } from '../../shared/routes.ts'
import { useMe } from '../hooks/index.ts'
import styles from './auth/auth.module.css'

export function CoursesPage(): JSX.Element {
  const app = useClient<Lab>()
  const navigate = useNavigate()
  const me = useMe()
  const courseId = me.data?.learner?.currentChapter?.courseId ?? null
  const [entry, setEntry] = useState<CourseCatalogEntry | null>(null)

  useEffect(() => {
    if (!courseId) {
      setEntry(null)
      return
    }
    let active = true
    app.courses
      .list()
      .then(entries => {
        if (active) setEntry(entries.find(c => c.id === courseId) ?? null)
      })
      .catch((err: unknown) => {
        console.error('Failed to load enrolled course', err)
        if (active) setEntry(null)
      })
    return () => {
      active = false
    }
  }, [app, courseId])

  return (
    <main className={styles.main}>
      <section className={styles.card}>
        <p className={styles.eyebrow}>Your course</p>

        {entry ? (
          <>
            <h1 className={styles.title}>{entry.title}</h1>
            <p className={styles.lede}>{entry.tagline}</p>
            <div className={styles.ssoStack}>
              <button
                className={styles.ssoButton}
                type="button"
                onClick={() => navigate(`/courses/${entry.id}`)}>
                <span style={{ flex: 1, textAlign: 'left' }}>
                  <strong>Continue</strong>
                </span>
                <span aria-hidden>→</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className={styles.title}>No active course yet.</h1>
            <p className={styles.lede}>
              Pick one from the catalog and your first lab spins up in seconds.
            </p>
            <div className={styles.ssoStack}>
              <button
                className={styles.ssoButton}
                type="button"
                onClick={() => navigate('/catalog')}>
                <span style={{ flex: 1, textAlign: 'left' }}>
                  <strong>Browse the catalog</strong>
                </span>
                <span aria-hidden>→</span>
              </button>
            </div>
          </>
        )}
      </section>
    </main>
  )
}
